/**
 * PagesData — Pages container behavior (slides, tabs, etc.)
 */

import type { BehaviorData } from "../behaviordata";
import type { TransformPath } from "../common";
import type { TimeTransition } from "./propertiesdata";

/** State name for the enter position (where objects start before animating in) */
export const PAGE_STATE_ENTER = '_pageEnter';
/** State name for the exit position (where objects go when leaving) */
export const PAGE_STATE_EXIT = '_pageExit';

/**
 * Which node's children are treated as the pages.
 * - `self` — the container's direct SceneObject children (default).
 * - `firstChild` — children of the container's first child TransformNode
 *   (e.g. a GLB root whose top-level nodes are the pages).
 */
export type PageSource = 'self' | 'firstChild';

/**
 * Pages container — each direct child SceneObject is one page, except the
 * children listed in `shared`. Only the active page is visible; shared
 * children stay visible on every page.
 *
 * Hierarchy:
 * ```
 * Presentation (Pages behavior, shared: ["./Model"])
 *   ├── Model           ← shared child: on every page, posed per page
 *   │   ├── Step 1         by `pages[].states`
 *   │   └── Step 2
 *   ├── Page 1          ← page 0 (visible by default)
 *   │   └── Caption
 *   └── Page 2          ← page 1 (hidden: authored `active: false`)
 *       └── Caption
 * ```
 *
 * Content of one page lives under that page's child. Content seen on
 * several pages (a model built up step by step, a nav bar) is a shared
 * child, and each page sets its look with `pages[].states`.
 *
 * Pages does NOT auto-hide children on load. Non-first pages must
 * be created with `active: false` so they start hidden. Entering play
 * mode applies the showing page's `pages[].states`.
 *
 * Navigation: use the `next` / `prev` / `showPage` actions, or BehaviorGraph
 * pagesNext / pagesPrev blocks. Active page index is 0-based.
 *
 * @action next - Next page
 * @action prev - Previous page
 * @action showPage - Go to a page. Args: (index, animate?) — index is 0-based, animate defaults to false
 * @action showAllPages - Show all pages
 */
export interface PagesData extends BehaviorData {
  "@type": "Pages";

  /**
   * Per-page transition configuration, positionally aligned with the page
   * children (`pages[i]` configures page i). Entries may be empty (`{}`) for
   * pages with no state assignments; a missing/short array means the remaining
   * pages have none.
   *
   * Example — a hero that exits on page 1, and a shared `Model` whose
   * `Step 2` group (with a `future` state that sets `active: false`) is
   * hidden on page 0 and back at base on page 1:
   * ```json
   * [
   *   { "states": [{ "target": "./Page1/Hero", "state": "entered" },
   *                { "target": "./Model/Step 2", "state": "future" }] },
   *   { "states": [{ "target": "./Page1/Hero", "state": "exitLeft" }] }
   * ]
   * ```
   */
  pages?: PageData[];

  /**
   * Default transition for page state changes, applied to any child state
   * that doesn't define its own transition.
   * Defaults to 0.5s easeInOut when omitted.
   */
  transition?: TimeTransition;

  /**
   * Ordering of outgoing vs incoming motion on an animated page change —
   * one axis, two choreographies (room for more, e.g. an enter-first
   * "cover" mode):
   *
   * - `"concurrent"` (default): today's overlap — the swap is immediate;
   *   the outgoing page's departure motion runs while the incoming page
   *   appears (crossfade model).
   * - `"exitFirst"`: the change sequences — every AnimationSequence exit
   *   clip across the outgoing page's subtree (per-slot `delay` honored,
   *   nested decks excluded — they own their pages) plays to completion
   *   while the incoming page stays hidden; the swap then runs as if the
   *   change had begun at that moment, and the autoplay dwell re-anchors
   *   there. A newer page change cancels a pending swap (last-intent
   *   wins); re-requesting the SAME target fast-forwards the swap without
   *   restarting the cascade; returning to the current page repairs the
   *   in-flight departures. Instant (non-animated) changes bypass
   *   entirely. With no exit clips on the outgoing page the swap is
   *   immediate — byte-for-byte the `"concurrent"` behavior.
   *
   * @default "concurrent"
   */
  transitionOrder?: 'concurrent' | 'exitFirst';

  /**
   * Advance to the next page when the user taps this deck's content in
   * play mode — page children and shared children alike. A tap that
   * something acts on fires that instead and never advances: a behavior
   * graph's onSelect listening on the tapped object or an ancestor,
   * runtime selection (Moveable, Rotatable, a `:selected` state), a
   * script, or a tap-to-play video. A collider or `:hover`/`:active`
   * states only make content tappable — such a tap still advances. Taps
   * on empty space never advance. Wrap follows `loop`
   * (off = hold on the last page, on = wrap to the first).
   * @default false
   */
  advanceOnTap?: boolean;

  /**
   * Automatically advance pages in play mode
   * @default false
   */
  autoplay?: boolean;

  /**
   * End-of-deck behavior: wrap from the last page back to the first.
   * Deck-level, not autoplay-only — it governs autoplay advancement AND
   * `advanceOnTap` wrapping alike.
   * @default false
   */
  loop?: boolean;

  /**
   * @deprecated Renamed to `loop` — it never was autoplay-only, and now also
   * governs `advanceOnTap` wrapping. Accepted as a legacy alias: folded into
   * `loop` and erased on load and on commit, so `getData()`, re-export, and
   * the editor all speak one field. Never written.
   */
  autoplayLoop?: boolean;

  /**
   * Duration each page is shown, in seconds. Drives autoplay advancement and
   * the Playable/Timeline mapping (seek, total duration) — it is the page's
   * timeline length, not autoplay-only config.
   * @default 5
   */
  pageDuration?: number;

  /**
   * Which node's children are the pages: `self` — the container's direct
   * SceneObject children (default); `firstChild` — children of the
   * container's first child TransformNode (e.g. a GLB root).
   * @default "self"
   */
  pageSource?: PageSource;

  /**
   * Shared children: paths (relative to the container) of direct children
   * that are NOT pages. A shared child is excluded from the page set — it
   * persists across page switches, is never activated/deactivated by page
   * navigation, and is animated per page via ordinary `pages[].states`
   * assignments targeting it. An unresolvable path excludes nothing (the
   * child stays a page). Only meaningful for `pageSource: "self"`; ignored
   * in `firstChild` mode.
   */
  shared?: TransformPath[];
}

/**
 * Per-page configuration. Lives at `PagesData.pages[i]` for page index `i`.
 */
export interface PageData {
  /**
   * State assignments applied when this page is shown: each target's
   * Properties behavior gets `setState(state)`. Objects assigned on any
   * page but not on the shown page revert to base (`setState(undefined)`).
   * One assignment per target — later duplicates override earlier ones.
   */
  states?: StateAssignment[];
}

/** One "set this object to this state" assignment on a page. */
export interface StateAssignment {
  /** Object whose state is set — path relative to the Pages container. */
  target: TransformPath;

  /** State name on the target's Properties behavior. */
  state: string;
}
